import React, {FC} from 'react';
import styled from 'styled-components';

import {computeSupportsCssAspectRatio} from '../../../libs/shared/helpers/src/browser/domHelpers';

/*
 * Props.
 */

interface ImageCommonProps {
  /** URL of the image to display. */
  src: string;
}

interface ImageWithAspectRatioProps extends ImageCommonProps {
  /** A string representing an aspect ratio (e.g: '16 / 9') */
  aspectRatio: string;
}

interface ImageWithWidthAndHeightProps extends ImageCommonProps {
  /** Width of the image to display (used to compute aspect ratio). */
  width: number;
  /** Height of the image to display (used to compute aspect ratio). */
  height: number;
}

type ImageProps = ImageWithAspectRatioProps | ImageWithWidthAndHeightProps;
function isPropsWithAspectRatio(props: ImageProps): props is ImageWithAspectRatioProps {
  return 'aspectRatio' in props;
}

/*
 * Style.
 */

const StyledImageWrapper = styled.div`
  height: 0;
  overflow: hidden;
  position: relative;
  margin-bottom: 16px;
`;
const StyledImageWithinWrapper = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: auto;
`;

const StyledImageWithAspectRatio = styled.img`
  width: 100%;
  height: auto;
  margin-bottom: 16px;
`;

/*
 * Component.
 */

export const Image: FC<ImageProps> = (props) => {
  const styledProps = getStylesProps(props);

  // If the browser supports the `aspect-ratio` property, we can get away with rendering the image directly.
  if (computeSupportsCssAspectRatio()) {
    return (
      <StyledImageWithAspectRatio
        src={props.src}
        style={{
          aspectRatio: styledProps.aspectRatio,
        }}
      />
    );
  }

  // Otherwise, we need to render a wrapper with a padding-bottom corresponding to the ratio of the image.
  return (
    <StyledImageWrapper
      style={{
        paddingBottom: styledProps.paddingBottom,
      }}
    >
      <StyledImageWithinWrapper src={props.src} />
    </StyledImageWrapper>
  );
};

function getStylesProps(props: ImageProps): {
  paddingBottom: string;
  aspectRatio: string;
} {
  if (isPropsWithAspectRatio(props)) {
    return {
      paddingBottom: `calc(${props.aspectRatio} * 100%)`,
      aspectRatio: props.aspectRatio,
    };
  }

  return {
    paddingBottom: `calc(${props.height} / ${props.width} * 100%)`,
    aspectRatio: `${props.width} / ${props.height}`,
  };
}
